/**
 * Aggregate head-to-head record for a rival card + VS header
 * (specs/challenge-flow-redesign — item #1 / AC-1.3a, AC-1.3b, T-40).
 *
 * The friends-list payload carries per-friend `wins` / `losses` / `totalChallenges` (completed 1v1
 * challenges, from the server's `getChallengeStatsBetweenUserAndFriends`). Draws aren't sent
 * explicitly: ties are counted in `totalChallenges` but excluded from both `wins` and `losses`, so
 * `draws = totalChallenges − wins − losses`. Pure — no data fetching.
 */

export interface RivalRecord {
  wins: number;
  draws: number;
  losses: number;
  /** True when there is at least one completed head-to-head challenge with this rival. */
  hasHistory: boolean;
}

interface RivalStatsInput {
  wins?: number;
  losses?: number;
  totalChallenges?: number;
}

export function getRivalRecord(friend: RivalStatsInput): RivalRecord {
  const wins = Math.max(0, Math.trunc(friend.wins ?? 0));
  const losses = Math.max(0, Math.trunc(friend.losses ?? 0));
  const total = Math.max(0, Math.trunc(friend.totalChallenges ?? 0));
  const draws = Math.max(0, total - wins - losses);
  return { wins, draws, losses, hasHistory: total > 0 };
}

/**
 * Short H2H summary for the VS header, e.g. "You lead 3–2" / "Behind 1–4" / "Level 2–2".
 * Returns null when there is no completed history (nothing to boast about yet).
 */
export function getH2hSummary(friend: RivalStatsInput): string | null {
  const { wins, losses, hasHistory } = getRivalRecord(friend);
  if (!hasHistory) return null;
  const score = `${wins}–${losses}`; // en-dash score, matches design
  if (wins > losses) return `You lead ${score}`;
  if (wins < losses) return `Behind ${score}`;
  return `Level ${score}`;
}
